import Service from '@ember/service';
import { type Registry as Services, service } from '@ember/service';
import type { Hotel } from 'frontend-utils/models';
import { HOTEL_COOKIE_NAME } from 'frontend-utils/utils/constants/constant-values';

type Subscription = {
  id: string;
  route: 'destination' | 'hotel' | 'market';
};

type Target = Subscription & {
  queryParams?: { otaId: string };
};

type SubscriptionCookie = {
  id: string;
  otaId?: string;
  type: 'hotel' | 'market';
};

const isSameSubscription = (a: Subscription, b?: Subscription): boolean =>
  // The API returns the default ids as numbers while route params are strings.
  a.route === b?.route && String(a.id) === String(b.id);

export default class DefaultSubscriptionService extends Service {
  @service declare cookie: Services['cookie'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  defaultDestinationSubscriptionId: string | null | undefined = null;
  defaultHotelId: string | null | undefined = null;

  // The only candidate when the app started with the last viewed cookie, as the defaults are
  // then never loaded.
  get cookieTarget(): Target | undefined {
    const cookie = this.cookie.getCookie(
      HOTEL_COOKIE_NAME,
    ) as SubscriptionCookie | null;

    if (!cookie?.id || (cookie.type !== 'hotel' && cookie.type !== 'market')) {
      return undefined;
    }

    return {
      route: cookie.type,
      id: String(cookie.id),
      queryParams: cookie.otaId ? { otaId: cookie.otaId } : undefined,
    };
  }

  get defaultTarget(): Target | undefined {
    const hotelId =
      this.defaultHotelId ?? this.store.peekAll<Hotel>('hotel')[0]?.id;

    if (hotelId) {
      return { route: 'hotel', id: String(hotelId) };
    }

    if (this.defaultDestinationSubscriptionId) {
      return {
        route: 'destination',
        id: String(this.defaultDestinationSubscriptionId),
      };
    }

    return undefined;
  }

  // Goes to the last viewed subscription first, then the default. Never goes to the subscription
  // that just failed to load, so a default that is itself inaccessible can't cause a redirect
  // loop. Returns whether a redirect was started.
  transitionToDefault(failed?: Subscription): boolean {
    const { cookieTarget, defaultTarget } = this;

    const target = [cookieTarget, defaultTarget].find(
      (candidate) => candidate && !isSameSubscription(candidate, failed),
    );

    if (!target) {
      return false;
    }

    if (cookieTarget && isSameSubscription(cookieTarget, failed)) {
      this.cookie.resetCookie(HOTEL_COOKIE_NAME);
    }

    if (target.queryParams) {
      this.router.transitionTo(target.route, target.id, {
        queryParams: target.queryParams,
      });
    } else {
      this.router.transitionTo(target.route, target.id);
    }

    return true;
  }
}

declare module '@ember/service' {
  interface Registry {
    'default-subscription': DefaultSubscriptionService;
  }
}
